import { FunctionComponent } from 'react';
import { Block, DocumentCites } from '@openlaw-au/types';
import FragmentRenderer from './fragmentRenderer';
import OrderedListRenderer from './orderedListRenderer';
import CitationRenderer from './citationRenderer';

interface Props {
  block: Block;
  numCitationsMap?: Record<number, number>;
  selection?: string;
  callback?: (value: any) => void;
  handleInViewChange?: (inView: boolean, block: Block) => void;
}

import React, {
  ReactDOM,
  WebViewHTMLAttributes
} from 'react'
import { useInView } from 'react-intersection-observer';


type CustomElement =
  React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>
  & {
    class?: string,
    // TODO: adding WebViewHTML + ReactDOM seems to fix issue, but need to do more research on
    //       proper typing of this
    children: React.DetailedHTMLProps<WebViewHTMLAttributes<any>, keyof ReactDOM>
  }

declare global {
  namespace JSX {
    interface IntrinsicElements {
      ['doc-title']: unknown
      ['catchword-block']: unknown
      ['quote-block']: unknown
    }
  }
}

const BlockRenderer: FunctionComponent<Props> = ({
  block,
  numCitationsMap,
  selection,
  callback,
  handleInViewChange
}) => {
  switch (block.type) {
    case 'TitleBlock': {
      return (
        <doc-title>
          <div className="text-2xl font-bold my-8">
            <FragmentRenderer frags={block.fragments} />
          </div>
        </doc-title>
      );
    }
    case 'CatchwordBlock': {
      return (
        <catchword-block>
          <div className="font-bold">
            {block.blocks.map((b, k) => {
              return (
                <div key={k} className="pb-10">
                  <BlockRenderer block={b} key={k} />
                </div>
              );
            })}
          </div>
        </catchword-block>
      );
    }
    case 'ChapterBlock': {
      let [label_meta, text_meta, id_meta] = block.meta;
      return (
        <div>
          <section className="text-2xl font-bold mb-2" id={id_meta.value}>
            {label_meta.value} {text_meta.value}
          </section>
          {block.blocks.map((b, k) => {
            return (
              <div key={k}>
                <BlockRenderer
                  block={b}
                  key={k}
                  selection={selection}
                  numCitationsMap={numCitationsMap}
                  callback={callback}
                  handleInViewChange={handleInViewChange}
                />
              </div>
            );
          })}
        </div>
      );
    }
    case 'DivisionBlock':
    case 'SubdivisionBlock':
    case 'PartBlock': {
      let [label_meta, text_meta, id_meta] = block.meta;

      const { ref, inView } = useInView({
        threshold: 1,
        onChange: (inView, _entry) => { handleInViewChange && handleInViewChange(inView, block) },
      });


      return (
        <div ref={ref} className="">
          {
            <section
              className="text-xl font-bold mb-2"
              id={id_meta?.value ?? ''}
            >
              {label_meta?.value ?? ''} {text_meta?.value ?? ''}
            </section>
          }
          {block.blocks.map((b, k) => {
            return (
              <div key={k}>
                <BlockRenderer
                  block={b}
                  key={k}
                  selection={selection}
                  numCitationsMap={numCitationsMap}
                  callback={callback}
                  handleInViewChange={handleInViewChange}
                />
              </div>
            );
          })}
        </div>
      );
    }
    case 'SubsectionBlock':
      let [label_meta, incoming_citation_meta] = block.meta;
      return (
        <div className={`pl-10 flex flex-row space-x-4 py-2`}>
          {
            <section className='min-w-[20px]'
              id={label_meta?.value ?? ''}
            >
              {label_meta?.value ?? ''}
            </section>
          }
          <div className="w-full grid-container grid grid-cols-12">
            <div
              className={`${selection ? 'col-span-12' : 'col-span-11'} ${incoming_citation_meta?.value === selection
                ? 'bg-[#F1F3EC] p-4 rounded-md'
                : ''
                }`}
            >
              <FragmentRenderer frags={block.fragments} />
              {block.blocks.map((b, k) => {
                return (
                  <BlockRenderer
                    block={b}
                    key={k}
                    selection={selection}
                    numCitationsMap={numCitationsMap}
                    callback={callback}
                  />
                );
              })}
            </div>
            {selection === '-1' ? (
              <div className="col-span-1 pl-10">
                {
                  incoming_citation_meta && <CitationRenderer
                  section={incoming_citation_meta.value}
                  numCitationsMap={numCitationsMap}
                  callback={callback}
                />
                }
                
              </div>
            ) : null}
          </div>
        </div>
      );
    case 'SectionBlock': {
      let [label_meta, text_meta, id_meta] = block.meta;

      const { ref, inView } = useInView({
        threshold: 1,
        onChange: (inView, _entry) => { handleInViewChange && handleInViewChange(inView, block) },
      });
      return (
        <div ref={ref} className={`flex flex-col`}>
          {
            <section
              className="text-xl font-bold mb-2 mr-3 mt-2"
              id={id_meta?.value ?? ''}
            >
              {label_meta?.value ?? ''} {text_meta?.value ?? ''}
            </section>
          }
          <div className="max-w-full grid-container grid grid-cols-12">
            <div
              className={`${selection ? 'col-span-12' : 'col-span-11'} ${label_meta.value === selection
                ? 'bg-[#F1F3EC] p-4 rounded-md'
                : ''
                }`}
            >
              {block.blocks.map((b, k) => {
                return (
                  <BlockRenderer
                    block={b}
                    key={k}
                    selection={selection}
                    numCitationsMap={numCitationsMap}
                    callback={callback}
                  />
                );
              })}
            </div>
            {selection === '-1' ? (
              <div className="col-span-1">
                <CitationRenderer
                  section={label_meta.value}
                  numCitationsMap={numCitationsMap}
                  callback={callback}
                />
              </div>
            ) : null}
          </div>
        </div>
      );
    }
    case 'ListItemBlock': {
      return (
        <div className="pt-4 flex flex-row space-x-2 my-4">
          <div>({block.meta[0]?.value})</div>
          <div className="pl-10">
            <FragmentRenderer frags={block.fragments} />
          </div>
        </div>
      );
    }
    case 'AkbnParagraph': {
      return (
        <div className='my-2'>
          <div>{block.meta[0]?.value}</div>
          <div>
            <FragmentRenderer frags={block.fragments} />
          </div>
        </div>
      );
    }
    case 'HeadingBlock': {
      return (
        <section
          className="pt-4 font-bold"
          id={block.fragments.map((frag) => frag.text).join('-')}
        >
          <FragmentRenderer frags={block.fragments} />
        </section>
      );
    }
    case 'SpeakerBlock': {
      return (
        <section
          className="pt-4 font-bold"
          id={block.fragments.map((frag) => frag.text).join('-')}
        >
          <FragmentRenderer frags={block.fragments} />
        </section>
      );
    }
    case 'OrderedListBlock': {
      return (
        <div className="pl-1">
          <OrderedListRenderer block={block} />
        </div>
      );
    }
    case 'QuoteBlock': {
      return (
        <quote-block>
          <div className="px-4 py-1 text-sm">
            {block.blocks.map((b, k) => (
              <BlockRenderer block={b} key={k} />
            ))}
            <FragmentRenderer frags={block.fragments} />
          </div>
        </quote-block>
      );
    }
    case 'TableBlock': {
      return (
        <table className="w-full text-sm text-left">
          {block.blocks.map((b, k) => (
            <BlockRenderer block={b} key={k} />
          ))}
          <FragmentRenderer frags={block.fragments} />
        </table>
      );
    }
    case 'TableHeadBlock': {
      return (
        <thead className="font-bold text-xs">
          {block.blocks.map((b, k) => (
            <BlockRenderer block={b} key={k} />
          ))}
          <FragmentRenderer frags={block.fragments} />
        </thead>
      );
    }
    case 'TableBodyBlock': {
      return (
        <tbody className="text-left">
          {block.blocks.map((b, k) => (
            <BlockRenderer block={b} key={k} />
          ))}
          <FragmentRenderer frags={block.fragments} />
        </tbody>
      );
    }
    case 'TableRowBlock': {
      return (
        <tr className="bg-white border-b even:bg-gray-50">
          {block.blocks.map((b, k) => (
            <BlockRenderer block={b} key={k} />
          ))}
          <FragmentRenderer frags={block.fragments} />
        </tr>
      );
    }
    case 'TableDataBlock': {
      return (
        <td className="px-6 py-4 text-black">
          {block.blocks.map((b, k) => (
            <BlockRenderer block={b} key={k} />
          ))}
          <FragmentRenderer frags={block.fragments} />
        </td>
      );
    }
    case 'TableHeadingBlock': {
      return (
        <th scope="row" className="px-6 py-4 font-medium whitespace-nowrap">
          {block.blocks.map((b, k) => (
            <BlockRenderer block={b} key={k} />
          ))}
          <FragmentRenderer frags={block.fragments} />
        </th>
      );
    }
    case 'SummaryBlock':
    case 'DecisionHeldBlock':
    case 'TextBlock': {
      return (
        <div className="flex flex-col my-2">
          {block.blocks.map((b, k) => {
            return <BlockRenderer block={b} key={k} />;
          })}
          <div>
            <FragmentRenderer frags={block.fragments} />
          </div>
        </div>
      );
    }
    case 'DefinitionBlock': {
      return (
        <div className="flex flex-col my-2">
          {block.blocks.map((b, k) => {
            return <BlockRenderer block={b} key={k} />;
          })}
          <div>
            <FragmentRenderer frags={block.fragments} />
          </div>
        </div>
      );
    }
    case 'UnOrderedList': {
      return (
        <div className="pl-14 pt-4">
          <OrderedListRenderer block={block} />
        </div>
      );
    }
    case 'FootnoteContainer': {
      return (
        <div className="flex flex-col text-xs">
          {block.blocks.map((b, k) => {
            return <BlockRenderer block={b} key={k} />;
          })}
          {/* <div> */}
          <FragmentRenderer frags={block.fragments} />
          {/* </div> */}
        </div>
      );
    }
    case 'UnknownBlock': {
      break;
    }
  }

  return null;
};

export default BlockRenderer;
